package com.eladfinish.bible.contest.screens.browse.usecases.chumash

import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.constants.BooleanConstants
import com.eladfinish.bible.contest.constants.SharedPrefsConstants
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.entities.Parashot
import com.eladfinish.bible.contest.extensions.highlightTextSpannedRange
import com.eladfinish.bible.contest.screens.browse.usecases.HaftarotUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.MakeOtiotAliyahSmallerUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.MarkAliyotUseCase
import com.eladfinish.bible.contest.screens.browse.usecases.PrepareChapterContentForDisplayUseCase
import com.russhwolf.settings.Settings
import kotlinx.collections.immutable.toImmutableList

class GetContentForChumashModeUseCase(
    private val prepareChapterContentForDisplayUseCase: PrepareChapterContentForDisplayUseCase,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
    private val getParashaContentUseCase: GetParashaContentUseCase,
    private val getParashaNameSpannedUseCase: GetParashaNameSpannedUseCase,
    private val markAliyotUseCase: MarkAliyotUseCase,
    private val haftarotUseCase: HaftarotUseCase,
    private val makeOtiotAliyahSmallerUseCase: MakeOtiotAliyahSmallerUseCase,
    private val settings: Settings,
) {

    operator fun invoke(
        bookIndex: Int,
        partIndex: Int,
        cachedParashot: Parashot,
        isTikunKorim: Boolean = false,
    ): ChapterData {
        val parashaName = cachedParashot.parashotNames[partIndex - 1]

        val parashaContent = getParashaContentUseCase(
            input = cachedParashot.parashotSources[partIndex - 1],
            bookIndex = bookIndex,
            onlyQri = settings.getBoolean(key = SharedPrefsConstants.ONLY_QRI_SWITCH, defaultValue = true),
            isTikunKorim = isTikunKorim,
        )

        val raw = markAliyotUseCase(parashaContent = parashaContent, areConnected = parashaName.contains("-"))

        val parashahContent = prepareChapterContentForDisplayUseCase(
            rawText = raw,
            isTikunKorim = isTikunKorim,
        )

        val spannedParashaName = parashaName
            .split("-")
            .map { it.trim() }
            .map { name ->
                getParashaNameSpannedUseCase.getParashaNameSpanned(
                    content = parashahContent,
                    parashaName = name,
                )
            }
            .map { (start, end) ->
                highlightTextSpannedRange(start = start, end = end)
            }

        val content = buildString {
            append(parashahContent)
            if (!isTikunKorim) {
                append("\n\n")
                val haftarah = haftarotUseCase.parseHaftarot(
                    parashaName = parashaName,
                    bookIndex = bookIndex,
                    eachVerseInNewLine = settings.getBoolean(
                        key = SharedPrefsConstants.KEY_EACH_VERSE_IN_NEW_LINE,
                        defaultValue = BooleanConstants.ENABLE_EACH_VERSE_IN_NEW_LINE,
                    ),
                    onlyQri = settings.getBoolean(
                        key = SharedPrefsConstants.ONLY_QRI_SWITCH,
                        defaultValue = true,
                    ),
                )
                append(haftarah)
            }
        }

        val spannedRangeList = buildList {
            // ORDER IS MATTER!
            addAll(elements = makeOtiotPasukSmallerUseCase(charSequence = content))
            addAll(elements = spannedParashaName)
            addAll(elements = makeOtiotAliyahSmallerUseCase(content))
        }

        return ChapterData(
            bookIndex = bookIndex,
            chapterIndex = partIndex,
            content = content,
            spannedRangeList = spannedRangeList.toImmutableList(),
        )
    }
}
